import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { CustomerActionMenu } from "Components/MenuActionButton/CustomerActionMenu";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  exportCustomerCSV,
  fetchCustomer as OnfetchCustomer,
} from "slices/customer/thunk";
import Swal from "sweetalert2";

const Customer = () => {
  document.title = "Customer List";
  const dispatch = useDispatch<any>();

  const selectCustomer = createSelector(
    (state: any) => state.customer, // <-- must match your reducer key
    (cust) => ({
      customer: cust.customerList,
      rowCount: cust.rowCount,
      loading: cust.loading,
      error: cust.error,
    })
  );

  const { customer, rowCount, loading, error } = useSelector(selectCustomer);
  const [customers, setCustomers] = useState<any[]>([]);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const customer_permissions = master_permissions?.modules_permissions?.find(
    (item: any) => item?.modules_name === app_modules?.master?.modules?.customer
  );

  //*// UserType with refreashed user(in permission fetch)
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.user_type === "Admin";

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_customer") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  
  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("customerPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("customerPage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);

  const [addInvoice, setAddInvoice] = useState<boolean>(false);
  const [editPackage, setEditPackage] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] = useState<boolean>();

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  useEffect(() => {
    dispatch(
      OnfetchCustomer({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, sortField, sortDirection]);

  const prevFilters = useRef(filterValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(filterValue)) {
      prevFilters.current = filterValue;
      dispatch(
        OnfetchCustomer({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    }
  }, [filterValue]);

  useEffect(() => {
    setData(customer);
  }, [customer]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_customer") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_customer: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("customerPage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const handleStatusToggle = async (dist: any) => {
    try {
      const updatedStatus = !dist.status;
      const customerId = dist.id || dist.customer_id || dist.pk; // fallback check

      if (!customerId) {
        toast.error("Customer ID missing");
        return;
      }

      const updatedDist = {
        id: customerId,
        status: updatedStatus,
      };

      setIsStatusUpdateLoading(true);

      const response: any = await Axios.patch(
        `/api/customers/edit-status/${customerId}/`,
        updatedDist
      );

      toast.success(
        `Customer ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      dispatch(
        OnfetchCustomer({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const { blob, filename } = await exportCustomerCSV({
        search,
        filters: filterValue,
      });
      // const { blob, filename } = await exportDistrictCSV();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDelete = useCallback(async (customerId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are You Sure You Want To Delete Customer!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      width: "350px",
      customClass: {
        popup: "swal2-sm-popup",
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Axios.delete(`/api/customers/delete/${customerId}/`);
      toast.success("Customer deleted successfully.");
      setCustomers((prev) => prev.filter((c) => c.customer_id !== customerId));
      
      if (data.length === 1 && pageIndex > 0) {
        handlePageChange(pageIndex - 1);
      } else {
        dispatch(
          OnfetchCustomer({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );
      }
    } catch (error) {
      console.error("Failed to delete customer", error);
      toast.error("Failed to delete customer.");
    }
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, search, filterValue]);

  const DynamicAction = useMemo(
    () => [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { stickyRight: true },
        cell: ({ row }: { row: any }) => {
          return (
            <CustomerActionMenu
              row={row}
              handleDelete={handleDelete}
              canEdit={customer_permissions?.permissions?.Update === true}
              canDelete={isAdmin}
            />
          );
        },
      },
    ],
    [handleDelete, isAdmin, customer_permissions]
  );

  const columns = [
    // {
    //   header: "Sr. No.",
    //   id: "sr_no", // important: since there's no accessorKey
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }: { row: any }) => {
    //     return row.index + 1 + pageIndex * pageSize;
    //   },
    // },
    {
      header: "Customer/Company Name",
      accessorKey: "customer_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/customers/column-filter-dropdown/?column_filter=customer_name",
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/customers/column-filter-dropdown/?column_filter=email",
    },
    {
      header: "Mobile Number",
      accessorKey: "mobile_no",
      enableColumnFilter: false,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/customers/column-filter-dropdown/?column_filter=mobile_no",
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const dist = row.original;

        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(dist)}
          >
            <i
              className={`bi ${
                dist.status
                  ? "bi-toggle-on text-primary"
                  : "bi-toggle-off text-danger"
              }`}
              style={{ color: "#0073BB" }}
            />
          </div>
        );
      },
    },

    {
      header: "PAN Number",
      accessorKey: "pan_number",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/customers/column-filter-dropdown/?column_filter=pan_number",
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value || "-";
      },
    },
    {
      header: "GST Number",
      accessorKey: "gstin",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/customers/column-filter-dropdown/?column_filter=gstin",
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value || "-";
      },
    },
    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by_name ?? "N/A";
        const date = formatDate(row.original.created_at);
        return `  ${name}  -  ${date}  `; // extra spaces for visual padding
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by_name ?? "N/A";
        const date = formatDate(row.original.updated_at);
        return `  ${name}  -  ${date}  `; // spaces before/after the name
      },
    },
    ...DynamicAction,
    // {
    //   header: "Action",
    //   enableColumnFilter: false,
    //   enableSorting: false,
    //   meta: { stickyRight: true },
    //   cell: ({ row }: { row: any }) => {
    //     const handleDelete = async (customerId: string) => {
    //       const result = await Swal.fire({
    //         title: "Are you sure?",
    //         text: "Are You Sure You Want To Delete Customer!",
    //         icon: "warning",
    //         showCancelButton: true,
    //         confirmButtonColor: "#d33",
    //         cancelButtonColor: "#3085d6",
    //         confirmButtonText: "Yes, delete it!",
    //         width: "350px",
    //         customClass: {
    //           popup: "swal2-sm-popup",
    //         },
    //       });

    //       if (!result.isConfirmed) return;

    //       try {
    //         await Axios.delete(`/api/customers/delete/${customerId}/`);
    //         toast.success("Customer deleted successfully.");
    //         setCustomers((prev) =>
    //           prev.filter((c) => c.customer_id !== customerId)
    //         );
    //         dispatch(
    //           OnfetchCustomer({
    //             page: pageIndex + 1,
    //             pageSize,
    //             sort: sortField,
    //             order: sortField ? sortDirection : "",
    //             search,
    //             filters: filterValue,
    //           })
    //         );
    //       } catch (error) {
    //         console.error("Failed to delete customer", error);
    //         toast.error("Failed to delete customer.");
    //       }
    //     };
    //     return <CustomerActionMenu row={row} handleDelete={handleDelete} />;
    //   },
    // },
  ];
  //   [dispatch, districts]
  // );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Customer/Company</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {customer_permissions?.permissions?.Create === true && (
                          <Link
                            to="/add-customer"
                            className="btn"
                            style={{
                              backgroundColor: "#0073BB",
                              borderColor: "#0073BB",
                              color: "#fff",
                            }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Add Customer/Company
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={customer}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      SearchPlaceholder="Search packages..."
                      colValue={setFilterValue}

                      scrollableFilterPopover={true}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default Customer;
